import { Builder } from '@builder.io/react';
import cn from 'classnames';
import { pipe } from 'fp-ts/lib/function';
import { FC } from 'react';

import { I18nInstance } from '@catawiki/cata-style';

import { fetchLotPrices, fetchLots } from '@/shared/api/buyer';
import { COLOR_NAME_WHITE } from '@/shared/constants/colors';
import {
  actionButtonInput,
  backgroundColorInput,
  componentLinkIdInput,
} from '@/shared/inputs';
import { enrichLotWithPriceData } from '@/shared/models/lot';
import { getBuilderSpacingClass } from '@/shared/spacing';
import { SharedBuilderComponent } from '@/shared/types';
import { getBuilderIOClassName } from '@/shared/utils/getBuilderIOClassName';
import { nullableData } from '@/shared/utils/http';
import { TRACER_NAME_BUILDER, getSpan } from '@/shared/utils/tracer';

import { ActionVariant, BaseAction, getActionVariantName } from '../Action';
import { ComponentHeader } from '../ComponentHeader/ComponentHeader';
import { UserLotFavoritesProvider } from '../providers/UserLotFavoritesProvider';
import sharedStyle from '../shared.module.scss';
import { Row } from './Row';
import { IdLot, LotInput, LotItem } from './types';

interface BuilderInput extends SharedBuilderComponent {
  componentLinkId?: string;
  title?: string;
  description?: string;
  action: BaseAction;
  actionVariant?: ActionVariant;
  lotIds: LotInput[];
}

type Props = BuilderInput & {
  locale?: string;
  lots: LotItem[];
  previewOnly?: boolean;
};

export const Lots: FC<Props> = ({
  componentLinkId,
  title,
  description,
  action,
  actionVariant = ActionVariant.NONE,
  lotIds = [],
  lots = [],
  backgroundColorClassName,
  builderBlock,
  builderState,
  locale,
  attributes,
  previewOnly = false,
}) => {
  const builderStyles = getBuilderIOClassName(attributes);
  const spacingStyles = getBuilderSpacingClass(builderBlock, builderState);

  // When open page in editor or LivePreview mode, Builder doesn't run dataFetcher
  // causing locale value undefined. So, we use builderState locale value when
  // using component in editor and leave the locale when we generate static content
  const blockLocale = locale || builderState?.context.locale || 'en';

  const idLots = lotIds.filter((item) => item.type === 'lot') as IdLot[];
  const initialFavorites = lots.flatMap((item) =>
    item.type === 'lot'
      ? [
          {
            lotId: item.id,
            count: item.favoriteCount,
          },
        ]
      : []
  );

  return (
    <section
      data-testid="lots"
      {...attributes}
      id={componentLinkId}
      className={cn(
        sharedStyle.section,
        builderStyles,
        spacingStyles,
        backgroundColorClassName
      )}
    >
      <ComponentHeader
        title={title}
        description={description}
        action={action}
        actionVariant={actionVariant}
        locale={blockLocale}
        headerClassName={cn('u-wrapper', {
          'u-m-b-xl u-m-b-lg-xxl-6': lotIds && lotIds.length > 0,
        })}
        mobileActionClassName={'u-wrapper'}
      >
        <UserLotFavoritesProvider
          lotIds={idLots.map((item) => item.lotId)}
          initialFavorites={initialFavorites}
          previewOnly={previewOnly}
        >
          <Row
            locale={blockLocale}
            lotIds={lotIds}
            lots={lots}
            previewOnly={previewOnly}
          />
        </UserLotFavoritesProvider>
      </ComponentHeader>
    </section>
  );
};

async function fetchLotItems(
  locale: string,
  lotIds: LotInput[],
  i18n: I18nInstance
): Promise<LotItem[]> {
  if (lotIds.length === 0) return [];

  const ids = lotIds
    .filter((item): item is IdLot => item.type === 'lot')
    .map((item) => item.lotId);

  const [eitherLots, eitherLotPrices] = await Promise.all([
    fetchLots(locale, ids),
    fetchLotPrices(ids),
  ]);

  const remoteLots = pipe(eitherLots, nullableData) || [];
  const lotPrices = pipe(eitherLotPrices, nullableData) || [];

  const lotsMap = remoteLots.reduce(
    (out, lot) => ({
      ...out,
      [lot.id]: {
        ...enrichLotWithPriceData(
          i18n,
          {
            id: lot.id,
            imageUrl: lot.original_image_url,
            lotUrl: lot.url,
            title: lot.title,
          },
          lotPrices
        ),
        type: 'lot',
      } as LotItem,
    }),

    {} as { [key in number]: LotItem }
  );

  return lotIds
    .map((item) =>
      item.type === 'lot' ? lotsMap[item.lotId] || null : item || null
    )
    .filter((item) => item);
}

export function dataFetcher(locale: string, i18n: I18nInstance) {
  return async ({ lotIds = [] }: BuilderInput) => {
    const span = getSpan(TRACER_NAME_BUILDER, 'Lots.dataFetcher');
    const lots = await fetchLotItems(locale, lotIds, i18n);
    span.end();
    return {
      locale,
      lots,
    };
  };
}

export const NAME = 'Lots';

export function componentRegister() {
  Builder.registerComponent(Lots, {
    name: `v2:${NAME}`,
    inputs: [
      componentLinkIdInput(),
      {
        name: 'title',
        type: 'CatawikiEditor',
        defaultValue:
          'Section <span class="u-typography-h2-serif tw:text-brand-primary">Title</span>',
      },
      {
        name: 'description',
        type: 'CatawikiEditor',
        defaultValue:
          'Section description indicating the content to be foundand presented here with a medium legth copy block.',
      },
      ...actionButtonInput({
        allowVariants: [ActionVariant.NONE, ActionVariant.TERTIARY],
      }),
      backgroundColorInput(COLOR_NAME_WHITE),
      {
        name: 'lotIds',
        type: 'list',
        friendlyName: 'Lots',
        subFields: [
          {
            name: 'type',
            friendlyName: 'Type',
            type: 'string',
            defaultValue: 'lot',
            enum: ['lot', 'text'],
          },
          {
            name: 'lotId',
            friendlyName: 'Lot ID',
            type: 'number',
            // A hack recommendation by builder, https://forum.builder.io/t/showif-in-subfields/2046
            showIf: `options.get('type') === 'lot'`,
          },
          {
            name: 'text',
            friendlyName: 'Text',
            type: 'CatawikiEditor',
            defaultValue:
              '<h3 class="u-typography-h3">Do you have <span class="p-serif tw:text-brand-primary">something similar to sell?</span></h3>',
          },
          {
            name: 'actionVariant',
            friendlyName: 'Button Type',
            type: 'string',
            defaultValue: ActionVariant.PRIMARY.toString(),
            enum: [
              ActionVariant.PRIMARY,
              ActionVariant.SECONDARY,
              ActionVariant.TERTIARY,
            ].map((name) => ({
              label: getActionVariantName(name),
              value: name,
            })),
          },
          {
            name: 'actionLabel',
            friendlyName: 'Button Label',
            type: 'CatawikiPlainEditor',
            defaultValue: 'Explore More',
          },
          {
            name: 'actionUrl',
            friendlyName: 'Button URL',
            type: 'CatawikiPlainEditor',
            defaultValue: 'https://www.catawiki.com',
          },
        ],
      },
    ],
    noWrap: true,
  });
  Builder.registerComponent(Lots, {
    name: `v3:${NAME}`,
    inputs: [
      componentLinkIdInput(),
      {
        name: 'title',
        type: 'html',
        defaultValue:
          'Section <span class="u-typography-h2-serif tw:text-brand-primary">Title</span>',
      },
      {
        name: 'description',
        type: 'html',
        defaultValue:
          'Section description indicating the content to be foundand presented here with a medium legth copy block.',
      },
      ...actionButtonInput({
        allowVariants: [ActionVariant.NONE, ActionVariant.TERTIARY],
        useCatawikiPlainEditor: false,
      }),
      backgroundColorInput(COLOR_NAME_WHITE),
      {
        name: 'lotIds',
        type: 'list',
        friendlyName: 'Lots',
        subFields: [
          {
            name: 'type',
            friendlyName: 'Type',
            type: 'string',
            defaultValue: 'lot',
            enum: ['lot', 'text'],
          },
          {
            name: 'lotId',
            friendlyName: 'Lot ID',
            type: 'number',
            // A hack recommendation by builder, https://forum.builder.io/t/showif-in-subfields/2046
            showIf: `options.get('type') === 'lot'`,
          },
          {
            name: 'text',
            friendlyName: 'Text',
            type: 'html',
            defaultValue:
              '<h3 class="u-typography-h3">Do you have <span class="p-serif tw:text-brand-primary">something similar to sell?</span></h3>',
          },
          {
            name: 'actionVariant',
            friendlyName: 'Button Type',
            type: 'string',
            defaultValue: ActionVariant.PRIMARY.toString(),
            enum: [
              ActionVariant.PRIMARY,
              ActionVariant.SECONDARY,
              ActionVariant.TERTIARY,
            ].map((name) => ({
              label: getActionVariantName(name),
              value: name,
            })),
          },
          {
            name: 'actionLabel',
            friendlyName: 'Button Label',
            type: 'CatawikiPlainEditorWithoutBuiltInTranslation',
            defaultValue: 'Explore More',
          },
          {
            name: 'actionUrl',
            friendlyName: 'Button URL',
            type: 'CatawikiPlainEditorWithoutBuiltInTranslation',
            defaultValue: 'https://www.catawiki.com',
          },
        ],
      },
    ],
    noWrap: true,
  });
  return `v2:${NAME}`;
}

export const componentDataFetchers = (locale: string, i18n: I18nInstance) => ({
  [`v2:${NAME}`]: dataFetcher(locale, i18n),
  [`v3:${NAME}`]: dataFetcher(locale, i18n),
});
